import { Primitive } from '../Primitive';
import { Type } from '../Type';
import { Point2D } from './Point2D';
import { Pose2D } from './Pose2D';
import { Size2D } from './Size2D';
function asNamedCoordinate(coordinates) {
    return Array.isArray(coordinates)
        ? { x: coordinates[0], y: coordinates[1] }
        : coordinates;
}
/**
 * An oriented 2D bounding box defined by a center pose and a size.
 *
 * The orientation is the angle in degrees that the box is rotated clockwise
 * on screen, in a coordinate system where:
 * - X axis points right
 * - Y axis points down (top-left origin)
 *
 * The frontend normalizes orientation to the [0°, 90°) range by rotating 90°
 * and swapping width/height as needed.
 *
 *
 * ```
 *    orientation = 0           orientation = 19
 *
 *   --x-----> AB = width      --x----->
 * |           AD = height   |        A
 * |                         |        '
 * y    A              B     y        ---'''
 * |     ''''''''''''''      |       '--------'''
 * |     '-----------'       |      '--------------'''   B
 * |     '-----------'       |      --------------------'
 * v     '-----O-----'       v     '--------------------
 *       '-----------'             --------------------'
 *       '-----------'            '----------O---------
 *      D              C          --------------------'
 *                               '--------------------
 *                            D   '''--------------'
 *                                       '''--------'
 *                                            '''---
 *                                                 '
 *                                                  C
 * ```
 */
export class Box2D extends Primitive {
    center;
    size;
    constructor(center, size) {
        super(Type.Box2D);
        this.center = center;
        this.size = size;
    }
    static fromCoordinates(topLeft, bottomRight, orientation = 0) {
        const tl = asNamedCoordinate(topLeft);
        const br = asNamedCoordinate(bottomRight);
        const centerX = (tl.x + br.x) / 2;
        const centerY = (tl.y + br.y) / 2;
        const width = br.x - tl.x;
        const height = br.y - tl.y;
        return new Box2D(new Pose2D(new Point2D(centerX, centerY), orientation), new Size2D(height, width));
    }
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiQm94MkQuanMiLCJzb3VyY2VSb290IjoiIiwic291cmNlcyI6WyIuLi8uLi8uLi8uLi8uLi9zcmMvbGliL3R5cGVzL2dlb21ldHJ5XzJkL0JveDJELnRzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQUFBLE9BQU8sRUFBRSxTQUFTLEVBQUUsTUFBTSxjQUFjLENBQUE7QUFDeEMsT0FBTyxFQUFFLElBQUksRUFBRSxNQUFNLFNBQVMsQ0FBQTtBQUU5QixPQUFPLEVBQUUsT0FBTyxFQUFFLE1BQU0sV0FBVyxDQUFBO0FBQ25DLE9BQU8sRUFBRSxNQUFNLEVBQUUsTUFBTSxVQUFVLENBQUE7QUFDakMsT0FBTyxFQUFFLE1BQU0sRUFBRSxNQUFNLFVBQVUsQ0FBQTtBQUtqQyxTQUFTLGlCQUFpQixDQUN4QixXQUFpRDtJQUVqRCxPQUFPLEtBQUssQ0FBQyxPQUFPLENBQUMsV0FBVyxDQUFDO1FBQy9CLENBQUMsQ0FBQyxFQUFFLENBQUMsRUFBRSxXQUFXLENBQUMsQ0FBQyxDQUFDLEVBQUUsQ0FBQyxFQUFFLFdBQVcsQ0FBQyxDQUFDLENBQUMsRUFBRTtRQUMxQyxDQUFDLENBQUMsV0FBVyxDQUFBO0FBQ2pCLENBQUM7QUFFRDs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7O0dBaUNHO0FBQ0gsTUFBTSxPQUFPLEtBQU0sU0FBUSxTQUFTO0lBQ3pCLE1BQU0sQ0FBUTtJQUNkLElBQUksQ0FBUTtJQUVyQixZQUFZLE1BQWMsRUFBRSxJQUFZO1FBQ3RDLEtBQUssQ0FBQyxJQUFJLENBQUMsS0FBSyxDQUFDLENBQUE7UUFDakIsSUFBSSxDQUFDLE1BQU0sR0FBRyxNQUFNLENBQUE7UUFDcEIsSUFBSSxDQUFDLElBQUksR0FBRyxJQUFJLENBQUE7SUFDbEIsQ0FBQztJQVlNLE1BQU0sQ0FBQyxlQUFlLENBQzNCLE9BQTZDLEVBQzdDLFdBQWlELEVBQ2pELGNBQXNCLENBQUM7UUFFdkIsTUFBTSxFQUFFLEdBQUcsaUJBQWlCLENBQUMsT0FBTyxDQUFDLENBQUE7UUFDckMsTUFBTSxFQUFFLEdBQUcsaUJBQWlCLENBQUMsV0FBVyxDQUFDLENBQUE7UUFFekMsTUFBTSxPQUFPLEdBQUcsQ0FBQyxFQUFFLENBQUMsQ0FBQyxHQUFHLEVBQUUsQ0FBQyxDQUFDLENBQUMsR0FBRyxDQUFDLENBQUE7UUFDakMsTUFBTSxPQUFPLEdBQUcsQ0FBQyxFQUFFLENBQUMsQ0FBQyxHQUFHLEVBQUUsQ0FBQyxDQUFDLENBQUMsR0FBRyxDQUFDLENBQUE7UUFDakMsTUFBTSxLQUFLLEdBQUcsRUFBRSxDQUFDLENBQUMsR0FBRyxFQUFFLENBQUMsQ0FBQyxDQUFBO1FBQ3pCLE1BQU0sTUFBTSxHQUFHLEVBQUUsQ0FBQyxDQUFDLEdBQUcsRUFBRSxDQUFDLENBQUMsQ0FBQTtRQUMxQixPQUFPLElBQUksS0FBSyxDQUNkLElBQUksTUFBTSxDQUFDLElBQUksT0FBTyxDQUFDLE9BQU8sRUFBRSxPQUFPLENBQUMsRUFBRSxXQUFXLENBQUMsRUFDdEQsSUFBSSxNQUFNLENBQUMsTUFBTSxFQUFFLEtBQUssQ0FBQyxDQUMxQixDQUFBO0lBQ0gsQ0FBQztDQUNGIn0=